import { Component, ElementRef, HostBinding, Input, OnInit } from '@angular/core';
import {
  ChangeSetItemType,
  FormattedChangesForItem,
  ItemChanges,
} from '@interfaces/change-sets/1.0';
import { tabPaths } from './index';
import { GetService } from '@services/utilities/1.0/get.service';
import { NgIf } from '@angular/common';

@Component({
  selector: 'txp-change-set-notice',
  templateUrl: './change-set-notice.component.html',
  styleUrls: ['./change-set-notice.component.scss'],
  standalone: true,
  imports: [NgIf],
})
export class ChangeSetNoticeComponent implements OnInit {
  // currently selected version by the user
  // TODO: make Observable
  @Input() activeVersion: ChangeSetItemType;
  // current changes for the selected version, or null if version is PUBLISHED
  // TODO: make Observable
  @Input() itemChanges: ItemChanges;
  // can be bullet or icon, bullet is used for tabs, icon is used for fields and changes inside a tab
  @Input() noticeType: string = 'bullet';
  // where to put the icons, either on the right or on top of the item that is wrapped
  @Input() noticeAlign: string = 'right';
  // a value that corresponds to a key in tabPaths that has an array value to check for changes
  // if this is provided, no need for customDecider
  @Input() pathType: string;
  // one or multiple keys corresponding to a full path (each level is separated by a dot ".") for a change
  @Input() fullPaths: string[];
  // an item to be sent in the event for reverting a specific change
  @Input() eventDetail: any;
  // a callback function that is used to determine if the current change set component should
  // show the icons that indicate that an item has been changed
  @Input() customDecider: (versionToCompare: string, item: FormattedChangesForItem) => boolean;
  // used in order to prevent the revert action
  @Input() preventRevert: boolean;

  @HostBinding('class') get hostClasses() {
    let noticeTypeClass = this.noticeType === 'bullet' ? 'bullet-notice' : 'icon-notice';
    let noticeAlignClass = this.noticeAlign === 'top' ? 'top-aligned' : 'right-aligned';
    let className = `${noticeTypeClass} ${noticeAlignClass}`;

    if (this.hasActiveVersionAndChanges()) {
      const versionClass =
        this.activeVersion === ChangeSetItemType.DRAFT ? 'draft-selected' : 'scheduled-selected';

      className = `${className} ${versionClass}`;
    }

    return className;
  }

  constructor(private hostElement: ElementRef) {}

  paths: string[];

  ngOnInit() {
    this.paths = tabPaths[this.pathType];
  }

  hasActiveVersionAndChanges(): boolean {
    if (!this.activeVersion || this.activeVersion === ChangeSetItemType.PUBLISHED) {
      return false;
    }

    return !!this.hasChanges(this.activeVersion);
  }

  revertSpecificChange() {
    const path = this.eventDetail || this.pathType;

    // this emits a native vent that is catched by: src/app/catalog/catalog-item-base/catalog-item-base.component.ts:90
    // TODO: make this an observable as well
    this.hostElement.nativeElement.dispatchEvent(
      new CustomEvent('changeSetRemove', { detail: path, bubbles: true }),
    );
  }

  hasChanges(changeType: ChangeSetItemType): boolean {
    const typeChanges = this.itemChanges?.[changeType];

    if (this.customDecider) {
      return this.customDecider(changeType, typeChanges);
    }

    if (Array.isArray(this.fullPaths)) {
      return this.fullPaths.some((path) => !!GetService.get(typeChanges, path));
    }

    if (!typeChanges) {
      return false;
    }

    const keyCase = this.paths?.some((path) => GetService.get(typeChanges, path, false));

    const directCheckPaths = ['product', 'productGroup', 'modifierGroup', 'composite'];

    if (directCheckPaths.includes(this.pathType)) {
      return !!typeChanges?.[this.pathType];
    }

    if (this.pathType === 'images') {
      return !!typeChanges?.decorators?.find((decorator) => decorator.field === 'images');
    }

    if (this.pathType === 'localization') {
      const hasLocalizationChanges = keyCase;
      const hasLocalizationDecoratorChanges = !!typeChanges.decorators?.find(
        (decorator) =>
          decorator.field === 'title' ||
          decorator.field === 'description' ||
          decorator.field === 'subTitle',
      );

      return hasLocalizationChanges || hasLocalizationDecoratorChanges;
    }

    if (this.pathType === 'upsell') {
      const hasUpsellChanges = keyCase;
      const hasUpsellDecoratorChanges = !!typeChanges.decorators?.find(
        (decorator) => decorator.field === 'upSellContext.description',
      );

      return hasUpsellChanges || hasUpsellDecoratorChanges;
    }

    return keyCase;
  }

  showDraftChanges(): boolean {
    if (this.activeVersion === ChangeSetItemType.DRAFT) {
      return false;
    }

    return this.hasChanges(ChangeSetItemType.DRAFT);
  }

  showScheduledChanges(): boolean {
    if (this.activeVersion === ChangeSetItemType.SCHEDULED) {
      return false;
    }

    return this.hasChanges(ChangeSetItemType.SCHEDULED);
  }
}
